import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Copy, Check, Trash2, KeyRound, AlertTriangle, ShieldCheck } from "lucide-react";
import { apiClient } from "@/services/api";
import { useToast } from "@/hooks/useToast";
import Modal from "@/components/ui/Modal";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
// Keep in sync with MAX_ACTIVE_API_KEYS_PER_USER in backend/src/models/ApiKey.ts
import { MAX_ACTIVE_API_KEYS_PER_USER } from "@/config/apiKeyPolicy";
import type { ApiKeyRecord } from "@/types";

// Keep in sync with scopes enforced in backend/src/middleware/auth.ts
const AVAILABLE_SCOPES = [
  { id: "events:read",        labelKey: "apiKeyScopeEventsRead" },
  { id: "events:write",       labelKey: "apiKeyScopeEventsWrite" },
  { id: "tracks:read",        labelKey: "apiKeyScopeTracksRead" },
  { id: "tracks:write",       labelKey: "apiKeyScopeTracksWrite" },
  { id: "devices:read",       labelKey: "apiKeyScopeDevicesRead" },
  { id: "devices:write",      labelKey: "apiKeyScopeDevicesWrite" },
];

const EXPIRY_OPTIONS = [
  { value: "",    labelKey: "apiKeyExpiryNone" },
  { value: "30",  labelKey: "apiKeyExpiry30Days" },
  { value: "90",  labelKey: "apiKeyExpiry90Days" },
  { value: "365", labelKey: "apiKeyExpiry1Year" },
  { value: "custom", labelKey: "apiKeyExpiryCustom" },
];

function minExpiryDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
}

function daysUntil(dateStr: string): number {
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.max(1, Math.ceil(diff / 86400000));
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (days < 30) return rtf.format(-days, "day");
  const months = Math.floor(days / 30);
  if (months < 12) return rtf.format(-months, "month");
  return rtf.format(-Math.floor(months / 12), "year");
}

function isKeyActive(key: ApiKeyRecord): boolean {
  if (key.revokedAt !== null) return false;
  if (key.expiresAt !== null && new Date(key.expiresAt) < new Date()) return false;
  return true;
}

function isKeyExpired(key: ApiKeyRecord): boolean {
  return key.revokedAt === null && key.expiresAt !== null && new Date(key.expiresAt) < new Date();
}

export default function ApiKeysTab() {
  const { t } = useTranslation(["auth", "common"]);
  const toast = useToast();

  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyScopes, setNewKeyScopes] = useState<string[]>([]);
  const [expiryOption, setExpiryOption] = useState("");
  const [customExpiryDate, setCustomExpiryDate] = useState("");
  const [nameError, setNameError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [revealedKeyName, setRevealedKeyName] = useState("");
  const [copied, setCopied] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState<ApiKeyRecord | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const data = await apiClient.getApiKeys();
      setKeys(data);
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const activeCount = keys.filter(isKeyActive).length;
  const atLimit = activeCount >= MAX_ACTIVE_API_KEYS_PER_USER;

  const resetCreateForm = () => {
    setNewKeyName("");
    setNewKeyScopes([]);
    setExpiryOption("");
    setCustomExpiryDate("");
    setNameError("");
  };

  const handleOpenCreate = () => {
    resetCreateForm();
    setShowCreate(true);
  };

  const handleCloseCreate = () => {
    setShowCreate(false);
    resetCreateForm();
  };

  const toggleScope = (scope: string) => {
    setNewKeyScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newKeyName.trim();
    if (!trimmed) {
      setNameError(t("auth:apiKeyNameRequired"));
      return;
    }
    setNameError("");

    let expiresInDays: number | undefined;
    if (expiryOption === "custom") {
      expiresInDays = customExpiryDate ? daysUntil(customExpiryDate) : undefined;
    } else if (expiryOption !== "") {
      expiresInDays = Number(expiryOption);
    }

    setIsCreating(true);
    try {
      const result = await apiClient.createApiKey({
        name: trimmed,
        scopes: newKeyScopes,
        expiresInDays,
      });
      setKeys((prev) => [result.record, ...prev]);
      setShowCreate(false);
      resetCreateForm();
      setRevealedKey(result.key);
      setRevealedKeyName(result.record.name);
      setCopied(false);
      toast.success(t("auth:apiKeyCreated"));
    } catch {
      toast.error(t("auth:apiKeyCreateError"));
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!revealedKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setIsRevoking(true);
    try {
      await apiClient.revokeApiKey(revokeTarget.id);
      setKeys((prev) =>
        prev.map((k) =>
          k.id === revokeTarget.id
            ? { ...k, revokedAt: new Date().toISOString() }
            : k
        )
      );
      setRevokeTarget(null);
      toast.success(t("auth:apiKeyRevokeSuccess"));
    } catch {
      toast.error(t("auth:apiKeyRevokeError"));
    } finally {
      setIsRevoking(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-secondary-500">
          {t("auth:apiKeyActiveCount", {
            active: activeCount,
            max: MAX_ACTIVE_API_KEYS_PER_USER,
          })}
        </p>
        <button
          type="button"
          onClick={handleOpenCreate}
          disabled={atLimit}
          className="flex items-center space-x-2 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" />
          <span>{t("auth:apiKeyCreate")}</span>
        </button>
      </div>

      {atLimit && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {t("auth:apiKeyLimitReached", { max: MAX_ACTIVE_API_KEYS_PER_USER })}
        </p>
      )}

      {/* Key list */}
      {loading && (
        <p className="text-sm text-secondary-500">{t("common:loading")}</p>
      )}

      {!loading && fetchError && (
        <p className="text-sm text-red-600 py-4 text-center">
          {t("auth:apiKeyLoadError")}
        </p>
      )}

      {!loading && !fetchError && keys.length === 0 && (
        <div className="text-center py-12 text-secondary-500">
          <KeyRound className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">{t("auth:apiKeyListEmpty")}</p>
        </div>
      )}

      {!loading && !fetchError && keys.length > 0 && (
        <ul className="space-y-2">
          {keys.map((key) => {
            const active = isKeyActive(key);
            const expired = isKeyExpired(key);
            const revoked = key.revokedAt !== null;

            return (
              <li
                key={key.id}
                className={`border rounded-lg overflow-hidden transition-opacity ${
                  active
                    ? "border-secondary-200 bg-white"
                    : "border-secondary-200 bg-secondary-50 opacity-60"
                }`}
              >
                <div className="flex items-start gap-3 px-4 py-3">
                  {/* Status dot */}
                  <div className="mt-1 shrink-0">
                    <span
                      className={`block h-2 w-2 rounded-full mt-0.5 ${
                        active
                          ? "bg-green-500"
                          : expired
                          ? "bg-amber-400"
                          : "bg-secondary-300"
                      }`}
                    />
                  </div>

                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span
                        className={`text-sm font-semibold ${
                          active ? "text-secondary-900" : "text-secondary-500"
                        }`}
                      >
                        {key.name}
                      </span>
                      {revoked && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-secondary-200 text-secondary-500">
                          {t("auth:apiKeyRevokedStatus")}
                        </span>
                      )}
                      {expired && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                          {t("auth:apiKeyExpiredStatus")}
                        </span>
                      )}
                      {key.scopes.map((scope) => (
                        <span
                          key={scope}
                          className="text-xs px-1.5 py-0.5 rounded-full bg-primary-50 text-primary-700 font-mono"
                        >
                          {scope}
                        </span>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-secondary-500">
                      <span>
                        {t("auth:apiKeyCreatedAt", { date: formatDate(key.createdAt) })}
                      </span>
                      {key.lastUsedAt ? (
                        <span>
                          {t("auth:apiKeyLastUsed", { date: timeAgo(key.lastUsedAt) })}
                          {key.usageCount > 0 && (
                            <> · {t("auth:apiKeyUsageCount", { count: key.usageCount })}</>
                          )}
                        </span>
                      ) : (
                        <span>{t("auth:apiKeyNeverUsed")}</span>
                      )}
                      {key.expiresAt && !expired && (
                        <span>
                          {t("auth:apiKeyExpiresAt", { date: formatDate(key.expiresAt) })}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Revoke action */}
                  {active && (
                    <button
                      type="button"
                      onClick={() => setRevokeTarget(key)}
                      className="shrink-0 p-1.5 text-secondary-400 hover:text-red-600 rounded transition-colors"
                      title={t("auth:apiKeyRevoke")}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Create modal */}
      <Modal
        isOpen={showCreate}
        onClose={handleCloseCreate}
        title={t("auth:apiKeyCreateTitle")}
        size="sm"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label
              htmlFor="apiKeyName"
              className="block text-sm font-medium text-secondary-700 mb-1"
            >
              {t("auth:apiKeyName")}
            </label>
            <input
              id="apiKeyName"
              type="text"
              maxLength={100}
              value={newKeyName}
              onChange={(e) => {
                setNewKeyName(e.target.value);
                if (nameError) setNameError("");
              }}
              placeholder={t("auth:apiKeyNamePlaceholder")}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                nameError ? "border-red-300" : "border-secondary-300"
              }`}
              autoFocus
            />
            {nameError && (
              <p className="mt-1 text-sm text-red-600">{nameError}</p>
            )}
          </div>

          <div>
            <p className="block text-sm font-medium text-secondary-700 mb-2">
              {t("auth:apiKeyScopes")}
            </p>
            <div className="space-y-2">
              {AVAILABLE_SCOPES.map((scope) => (
                <label
                  key={scope.id}
                  className="flex items-start space-x-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={newKeyScopes.includes(scope.id)}
                    onChange={() => toggleScope(scope.id)}
                    className="mt-0.5 text-primary-600 focus:ring-primary-500 rounded"
                  />
                  <div>
                    <span className="text-sm font-mono text-secondary-800">
                      {scope.id}
                    </span>
                    <p className="text-xs text-secondary-500">
                      {t(`auth:${scope.labelKey}` as Parameters<typeof t>[0])}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="apiKeyExpiry"
              className="block text-sm font-medium text-secondary-700 mb-1"
            >
              {t("auth:apiKeyExpiry")}
            </label>
            <select
              id="apiKeyExpiry"
              value={expiryOption}
              onChange={(e) => {
                setExpiryOption(e.target.value);
                if (e.target.value !== "custom") setCustomExpiryDate("");
              }}
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              {EXPIRY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(`auth:${opt.labelKey}` as Parameters<typeof t>[0])}
                </option>
              ))}
            </select>
            {expiryOption === "custom" && (
              <input
                type="date"
                min={minExpiryDate()}
                value={customExpiryDate}
                onChange={(e) => setCustomExpiryDate(e.target.value)}
                className="mt-2 w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={handleCloseCreate}
              className="px-4 py-2 text-sm text-secondary-700 border border-secondary-300 rounded-lg hover:bg-secondary-50"
            >
              {t("common:cancel")}
            </button>
            <button
              type="submit"
              disabled={isCreating}
              className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isCreating ? t("common:loading") : t("auth:apiKeyCreate")}
            </button>
          </div>
        </form>
      </Modal>

      {/* Reveal key modal */}
      <Modal
        isOpen={revealedKey !== null}
        onClose={() => setRevealedKey(null)}
        title={t("auth:apiKeyRevealTitle")}
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-start space-x-3 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>{t("auth:apiKeyRevealWarning")}</p>
          </div>

          {revealedKeyName && (
            <p className="text-sm text-secondary-500">
              <span className="font-medium text-secondary-900">{revealedKeyName}</span>
            </p>
          )}

          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0 px-3 py-2.5 bg-secondary-900 rounded-lg overflow-x-auto">
              <code className="text-sm font-mono text-secondary-100 whitespace-nowrap select-all">
                {revealedKey}
              </code>
            </div>
            <button
              type="button"
              onClick={handleCopy}
              className={`shrink-0 flex items-center space-x-1.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                copied
                  ? "bg-green-100 text-green-700"
                  : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
              }`}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" />
                  <span>{t("auth:apiKeyCopied")}</span>
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  <span>{t("auth:apiKeyCopy")}</span>
                </>
              )}
            </button>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={() => setRevealedKey(null)}
              className="flex items-center space-x-2 px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <ShieldCheck className="h-4 w-4" />
              <span>{t("auth:apiKeyRevealDone")}</span>
            </button>
          </div>
        </div>
      </Modal>

      {/* Revoke confirmation modal */}
      <ConfirmationModal
        isOpen={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevoke}
        title={t("auth:apiKeyRevokeTitle")}
        message={t("auth:apiKeyRevokeConfirm", { name: revokeTarget?.name ?? "" })}
        confirmButtonText={t("auth:apiKeyRevoke")}
        confirmButtonColor="red"
        isLoading={isRevoking}
      />
    </div>
  );
}
